import { перевестиОтказ } from '../i18n/server.js';
import { экранУзкий } from '../composables/useSmallScreen.js';
import { to } from '../i18n/office.js';

/* Клиент HTTP. Через него идут запросы, инициированные человеком:
   справочники, списки, аналитика. Поток событий цеха живёт в socket.js. */

const BASE = import.meta.env.VITE_API_URL || '/api';
const TOKEN_KEY = 'mes-token';

export const tokenStore = {
  get: () => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } },
  set: (t) => { try { t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY); } catch { /* приватный режим */ } },
};

export class ApiError extends Error {
  constructor(message, { status, body, offline = false } = {}) {
    /* Причина отказа приходит с сервера по-русски. Переводим здесь, в одном
       месте на все экраны: иначе пришлось бы трогать каждое место, где
       показывают e.message. Исходный текст сохраняем — по нему ищут в логе
       и на него опираются проверки. */
    super(перевестиОтказ(message));
    this.исходный = message;
    this.status = status;
    this.body = body;
    this.offline = offline;
    /* Признак для очереди: что имеет смысл повторять.

       409 сам по себе НЕ повторяемый: им отвечают и «идемпотентный запрос ещё
       выполняется» (повторить нужно), и «превышение выпуска» или «операция
       закрыта» (повтор никогда не пройдёт). Различаем по флагу retryable,
       который сервер ставит только в первом случае. Иначе очередь молотила бы
       восемь попыток с нарастающей паузой и показывала оператору не ту причину. */
    this.retryable = offline || status === 429 || status >= 500 || body?.retryable === true;
  }
}

/* Обработчик потери авторизации. Ставится в auth-хранилище, чтобы http.js
   не зависел от Pinia и роутера. */
let onUnauthorized = null;
export const setUnauthorizedHandler = (fn) => { onUnauthorized = fn; };

/* Тот же путь для сокета: истёкший токен рвёт и его тоже, и человека надо
   увести на вход одинаково — независимо от того, какой канал заметил первым. */
export const signalUnauthorized = (reason) => { onUnauthorized?.(reason); };

/* Ключ можно передать явно. Нужно ровно одному вызывающему — цеховому
   табло: у него нет сессии человека, ключ живёт своей жизнью и не должен
   попадать в общее хранилище, где его подхватит восстановление сессии. */
/* ------------------------ телефон — только просмотр ------------------------

   Решение заказчика: MES, открытый в браузере телефона, работает на чтение
   (composables/useSmallScreen.js). Раньше режим включал каждый экран сам —
   и проверка тёмной темы и телефона нашла, что включили его три экрана из
   четырнадцати. С телефона можно было завести заказ, выдать бирки, завести
   тару, начать обслуживание и отключить узел оборудования.

   Запрет поэтому стоит здесь, в одном месте на все экраны: новый экран
   не может его забыть, а прежние не могут его обойти.

   ЧТО ПРОПУСКАЕМ. Не всякий POST что-то меняет. Вход и выход меняют сессию,
   а не данные цеха; расчёт навески считает и ничего не пишет; открыть
   сменный отчёт на проверку и пересчитать его — это посмотреть итоги, ради
   которых руководство и открывает систему с телефона. Список короткий
   и поимённый: всё, чего в нём нет, считается изменением. Ошибиться
   в сторону «запрещено» безопаснее, чем в сторону «разрешено».

   ЧЕГО ЭТО НЕ ЗАКРЫВАЕТ. Это защита от нажатия на бегу, а не граница прав:
   сервер ширину экрана не видит, и запрос, отправленный в обход интерфейса,
   пройдёт. Права по-прежнему держит сервер — ролями. */
const ЧИТАЮТ_POST = [
  /^\/auth\//,
  /^\/melt\/preview$/,
  /^\/shift-reports$/,
  /^\/shift-reports\/[^/]+\/recalc$/,
];

function запретСТелефона(method, path) {
  if (String(method).toUpperCase() === 'GET' || !экранУзкий()) return null;
  const чистый = String(path).split('?')[0];
  if (ЧИТАЮТ_POST.some((образец) => образец.test(чистый))) return null;
  /* Не повторяемая ошибка с отдельным признаком: очередь не должна молотить
     её восемь раз, а экран — показывать её как отказ сервера. */
  return new ApiError(to('Телефон — только просмотр. Изменения делаются с планшета или компьютера'), {
    status: 403, body: { readOnly: true },
  });
}

async function request(method, path, { body, headers = {}, signal, timeout = 15000, token: явныйКлюч } = {}) {
  const запрет = запретСТелефона(method, path);
  if (запрет) throw запрет;

  const token = явныйКлюч || tokenStore.get();

  /* Свой таймаут: у зависшего Wi-Fi запрос может висеть минутами, а оператор
     за это время нажмёт кнопку ещё пять раз. */
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(new Error('timeout')), timeout);
  if (signal) signal.addEventListener('abort', () => ctrl.abort(), { once: true });

  let res;
  try {
    res = await fetch(BASE + path, {
      method,
      headers: {
        ...(body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: 'Bearer ' + token } : {}),
        ...headers,
      },
      body: body ? JSON.stringify(body) : undefined,
      signal: ctrl.signal,
    });
  } catch (e) {
    clearTimeout(timer);
    // Сеть недоступна или таймаут — это повторяемая ошибка
    throw new ApiError('Нет связи с сервером', { offline: true });
  } finally {
    clearTimeout(timer);
  }

  let payload = null;
  const text = await res.text();
  if (text) { try { payload = JSON.parse(text); } catch { payload = { raw: text }; } }

  if (res.status === 401) {
    /* Причину передаём наверх: «Сессия истекла» и «Учётная запись недоступна»
       требуют от человека разных действий, и экран входа должен сказать,
       что именно случилось. Иначе выход из системы выглядит случайным. */
    onUnauthorized?.(перевестиОтказ(payload?.error || 'Требуется вход'));
    throw new ApiError(payload?.error || 'Требуется вход', { status: 401, body: payload });
  }
  if (!res.ok) {
    throw new ApiError(payload?.error || `Ошибка ${res.status}`, { status: res.status, body: payload });
  }

  // Повтор из очереди возвращает сохранённый ответ — помечаем, чтобы не считать заново
  if (res.headers.get('idempotent-replay') === 'true' && payload && typeof payload === 'object') {
    payload.__replay = true;
  }
  return payload;
}

const qs = (params) => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params || {})) {
    if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  }
  const s = p.toString();
  return s ? '?' + s : '';
};

/* Выгрузка файла. Отдельным методом, а не через `get`: тот разбирает ответ
   как JSON, а здесь нужны байты. Через fetch с токеном, а не ссылкой <a href>:
   в ссылку не поставить заголовок авторизации, а отдавать выгрузку по одному
   URL без проверки прав значит выложить данные любому, кто угадает адрес. */
async function blob(path, params, { timeout = 60000 } = {}) {
  const token = tokenStore.get();
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(new Error('timeout')), timeout);

  try {
    const res = await fetch(BASE + path + qs(params), {
      headers: token ? { Authorization: 'Bearer ' + token } : {},
      signal: ctrl.signal,
    });
    if (!res.ok) {
      // Ошибку сервер отдаёт JSON-ом даже там, где успех — это файл
      let message = `Ошибка ${res.status}`;
      try { message = (await res.json())?.error || message; } catch { /* не JSON */ }
      throw new ApiError(message, { status: res.status });
    }
    return await res.blob();
  } finally {
    clearTimeout(timer);
  }
}

/* Справочник для формы: список плюс причина, если он не загрузился.

   Обычное `.catch(() => ({ items: [] }))` превращает «не смогли загрузить»
   в «ничего нет». Для ленты это терпимо, для выпадающего списка в форме —
   нет: человек открывает диалог, видит пустой выбор, и ничто не говорит
   ему, что дело не в пустом справочнике, а в неудачном запросе. Он ищет
   пропавшие причины брака в настройках, которых никто не трогал.

   Возвращаем и то, и другое: список — чтобы форма работала, ошибку —
   чтобы форма о ней сказала. */
export const списком = (запрос) => запрос
  .then((r) => ({ items: r?.items || [], ошибка: null }))
  .catch((e) => ({ items: [], ошибка: e?.message || String(e) }));

export const api = {
  get: (path, params, opts) => request('GET', path + qs(params), opts),
  post: (path, body, opts) => request('POST', path, { ...opts, body }),
  patch: (path, body, opts) => request('PATCH', path, { ...opts, body }),
  /* Удаление в системе редкость: мастер-данные не удаляются, а отключаются.
     Но записи производственного календаря удалять надо — они ни на что
     не ссылаются и ничего не объясняют задним числом. Метод назван del,
     а не delete: delete — зарезервированное слово, и обращение api.delete()
     работает, а деструктуризация { delete } — уже нет. */
  del: (path, opts) => request('DELETE', path, opts),
  blob,
  qs,
};
